<template>
  <v-dialog v-model="openProfileDialog" max-width="1000px" persistent>
    <div class="profile-container">
      <v-container>
        <v-row justify="space-between">
          <h1 class="profile-title">
            {{ $t('profile.title') }}
          </h1>
          <v-btn color="secondary" icon @click="closeDialog">
            <v-icon>mdi-window-close</v-icon>
          </v-btn>
        </v-row>
        <v-row>
          <v-col cols="12" md="3">
            <div class="field-title">{{ $t('fields.name') }}:</div>
            <div class="field-value big">{{ leader.name }}</div>
          </v-col>

          <v-col cols="12" md="3">
            <div class="field-title">{{ $t('fields.lastname') }}:</div>
            <div class="field-value big">{{ leader.lastname }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="field-title">{{ $t('fields.unity') }}:</div>
            <div class="field-value big">
              {{ getVisibleUnities(leader.unities) }}
            </div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="field-title">{{ $t('fields.range') }}:</div>
            <div class="profile-rating">
              <v-tooltip left>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-icon
                      :class="getRangeStars(leader.range) >= 1 ? 'filled' : ''"
                    >
                      mdi-star
                    </v-icon>
                    <v-icon
                      :class="getRangeStars(leader.range) >= 2 ? 'filled' : ''"
                    >
                      mdi-star
                    </v-icon>
                    <v-icon
                      :class="getRangeStars(leader.range) >= 3 ? 'filled' : ''"
                    >
                      mdi-star
                    </v-icon>
                    <v-icon
                      :class="getRangeStars(leader.range) >= 4 ? 'filled' : ''"
                    >
                      mdi-star
                    </v-icon>
                    <v-icon
                      :class="getRangeStars(leader.range) >= 5 ? 'filled' : ''"
                    >
                      mdi-star
                    </v-icon>
                  </span>
                </template>
                <span>{{ translateListValue(leader.range) }}</span>
              </v-tooltip>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="3">
            <div class="field-title">{{ $t('fields.country') }}:</div>
            <div class="field-value">{{ leader.country }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="field-title">{{ $t('fields.city') }}:</div>
            <div class="field-value">{{ leader.city }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="field-title">{{ $t('reg.id') }}:</div>
            <div class="field-value">{{ leader.idNumber }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="field-title">{{ $t('fields.mobile') }}:</div>
            <div class="field-value">{{ leader.phone }}</div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <div class="field-title">{{ $t('fields.email') }}:</div>
            <div class="field-value">{{ leader.email }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="field-title">{{ $t('reg.nativeLang') }}:</div>
            <div class="field-value">
              {{ translateListValue(leader.firstLanguage) }}
            </div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="field-title">{{ $t('reg.secondLang') }}:</div>
            <div class="field-value">
              {{ getVisibleValue(leader.secondLanguage, true) }}
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <div class="field-title">{{ $t('fields.profile') }}:</div>
            <div class="field-value">
              {{ getVisibleValue(leader.profiles, true) }}
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="field-title">{{ $t('reg.associations') }}:</div>
            <div class="field-value">
              {{ getVisibleValue(leader.cientificAs) }}
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <div class="field-title">{{ $t('fields.profession') }}:</div>
            <div class="field-value">
              {{ getVisibleValue(leader.professions, true) }}
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="field-title">{{ $t('reg.postgrade') }}:</div>
            <div class="field-value">
              {{ getVisibleValue(leader.postGrades, true) }}
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <div class="field-title">{{ $t('fields.specialty') }}:</div>
            <div class="field-value">
              {{ getVisibleValue(leader.specialties, true) }}
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="field-title">{{ $t('fields.subSpecialty') }}:</div>
            <div class="field-value">
              {{ getVisibleValue(leader.subSpecialties, true) }}
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <div class="field-title">{{ $t('fields.criteria') }}:</div>
            <div class="field-value">
              {{ getVisibleValue(leader.criteria, true) }}
            </div>
          </v-col>
          <v-col cols="12" md="6" v-if="isAdmin">
            <div class="field-title">{{ $t('fields.endContractDate') }}:</div>
            <div class="field-value">
              {{ getDateFromString(leader.endContract) }}
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <div class="field-title">{{ $t('fields.observations') }}:</div>
            <div class="field-value">
              {{ getVisibleValue(leader.observations) }}
            </div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="field-title">{{ $t('fields.createdDate') }}:</div>
            <div class="field-value small">
              {{ getDateFromTimestamp(leader.createdAt) }}
            </div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="field-title">{{ $t('fields.updatedDate') }}:</div>
            <div class="field-value small">
              {{ getDateFromTimestamp(leader.updatedAt) }}
            </div>
          </v-col>
        </v-row>
        <v-row v-if="leader.publications && leader.publications.length">
          <v-col cols="12">
            <div class="field-title">{{ $t('reg.publications') }}:</div>
            <div class="field-value">
              <div
                v-for="(p, index) in leader.publications"
                :key="`pub-${index}`"
                class="publication-item-container"
              >
                <div class="publication-item">
                  <div>
                    <span class="bullet">&bull;</span>
                    <span class="publication-name">{{ p.name }}</span>
                  </div>
                  <div class="publication-info">
                    {{ getPublicationInfo(p) }}
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
        <v-divider class="mt-4 mb-4"></v-divider>
        <v-row justify="center">
          <v-btn
            class="mr-4"
            color="secondary"
            @click="openCV"
            v-show="leader.cvUrl"
          >
            {{ $t('profile.seeCv') }}
          </v-btn>
          <v-btn class="mr-4" color="secondary" @click="exportEventsHistory">
            {{ $t('profile.eventsHistory') }}
          </v-btn>
          <v-btn
            class="mr-4"
            @click="editProfile"
            color="primary"
            v-if="isAdmin"
          >
            {{ $t('admin.editProfile') }}
          </v-btn>
          <v-btn class="mr-4 register-button" @click="closeDialog">
            {{ $t('common.close') }}
          </v-btn>
        </v-row>
      </v-container>
      <v-dialog v-model="showNoEventsDialog" max-width="420px">
        <v-card>
          <v-card-text class="pt-6">
            {{ $t('profile.noEventsHistory') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="showNoEventsDialog = false">
              {{ $t('common.close') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-dialog>
</template>

<script>
import _clone from 'lodash/cloneDeep'
import { mapState } from 'vuex'
import {
  getVisibleValue,
  getVisibleUnities,
  getDateFromTimestamp,
  getDateFromString,
  getPublicationInfo,
  translateListValue
} from '@/utils/common'
import { auth } from '../firebase'
import { isUserAdmin } from '@/consts/common'
import XLSX from 'xlsx'
import { saveAs } from 'file-saver'

export default {
  data: () => ({
    leader: {},
    showNoEventsDialog: false,
    isAdmin: isUserAdmin(auth.currentUser.email)
  }),
  watch: {
    selectedLeader(newVal) {
      if (newVal) {
        this.leader = _clone(newVal)
      } else {
        this.leader = {}
      }
    },
    openProfileDialog(newVal) {
      if (newVal) {
        this.fetchEventsIfNeeded()
        setTimeout(() => {
          document.getElementsByClassName(
            'v-dialog v-dialog--active'
          )[0].scrollTop = 0
        }, 100)
      } else {
        this.leader = {}
      }
    }
  },

  computed: {
    ...mapState({
      openProfileDialog: state => state.leaderStore.openProfileDialog,
      registering: state => state.leaderStore.registering,
      selectedLeader: state => state.leaderStore.selectedLeader,
      leadersMap: state => state.leaderStore.leadersMap,
      events: state => state.eventStore.events,
      loadingEvents: state => state.eventStore.loadingEvents
    }),
    leaderEvents() {
      if (!this.leader.id) return []
      return this.events.filter(
        event =>
          Array.isArray(event.leaders) && event.leaders.includes(this.leader.id)
      )
    },
    eventHistoryHeaders() {
      return [
        { text: this.$t('calendar.name'), value: 'name' },
        { text: this.$t('calendar.start'), value: 'start' },
        { text: this.$t('calendar.end'), value: 'end' },
        { text: this.$t('calendar.assoLeaders'), value: 'leadersText' },
        { text: this.$t('calendar.description'), value: 'description' }
      ]
    }
  },
  methods: {
    getDateFromTimestamp: getDateFromTimestamp,
    getVisibleValue: getVisibleValue,
    getVisibleUnities: getVisibleUnities,
    getPublicationInfo: getPublicationInfo,
    translateListValue: translateListValue,
    getDateFromString: getDateFromString,
    getRangeStars(range) {
      const ranges = [
        'Heavy Users / Adopters',
        'Advocates Locales',
        'Emerging KOLs',
        'National & Regional KOLs',
        'TOP KOLs (Élite Global)'
      ]
      const index = ranges.indexOf(range)
      return index >= 0 ? index + 1 : 0
    },
    closeDialog() {
      this.$store.dispatch('leaderStore/openProfileDialog', false)
    },
    fetchEventsIfNeeded() {
      this.$store.dispatch('eventStore/fetchEventsIfNeeded', this.leadersMap)
    },
    editProfile() {
      this.$store.dispatch('leaderStore/editLeader', {
        leader: this.leadersMap[this.selectedLeader.id]
      })
    },
    openCV() {
      this.$store.dispatch('leaderStore/openPdf', {
        url: this.leader.cvUrl,
        name: this.leader.name + ' ' + this.leader.lastname
      })
    },
    getExportableEventsData(events) {
      return events.map(event =>
        this.eventHistoryHeaders.reduce(
          (acc, header) => ({
            ...acc,
            [header.text]: event[header.value]
          }),
          {}
        )
      )
    },
    getExcelBuffer(data) {
      const worksheet = XLSX.utils.json_to_sheet(data)
      const workbook = XLSX.utils.book_new()
      worksheet['!cols'] = [200, 100, 100, 250, 500].map(width => ({
        wpx: width
      }))

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        this.$t('profile.eventsHistory').substring(0, 31)
      )
      const content = XLSX.write(workbook, {
        bookType: 'xlsx',
        bookSST: true,
        type: 'binary'
      })
      const buffer = new ArrayBuffer(content.length)
      const view = new Uint8Array(buffer)

      for (let i = 0; i !== content.length; i++) {
        view[i] = content.charCodeAt(i) & 0xff
      }

      return buffer
    },
    exportEventsHistory() {
      try {
        if (!this.leaderEvents.length) {
          this.showNoEventsDialog = true
          return
        }

        const leaderName = [this.leader.name, this.leader.lastname]
          .filter(Boolean)
          .join(' ')
        const fileName = `${this.$t('profile.eventsHistory')} - ${leaderName}`
        const data = this.getExportableEventsData(this.leaderEvents)
        const buffer = this.getExcelBuffer(data)
        const blob = new Blob([buffer], {
          type: 'application/octet-stream'
        })

        saveAs(blob, fileName + '.xlsx')
      } catch (e) {
        return e
      }
    }
  }
}
</script>
<style lang="scss">
.profile-container {
  display: flex;
  justify-content: center;
  background-color: #fff;
  padding: 20px;
  word-break: break-word;

  .field-title {
    font-size: 13px;
    color: gray;
  }

  .field-value {
    font-size: 18px;
    font-weight: bold;

    &.big {
      font-size: 22px;
    }
    &.small {
      color: #8e8e8e;
      font-size: 16px;
    }
  }

  .profile-rating {
    .v-icon.v-icon {
      font-size: 32px;
      color: #c7c7c7;

      &.filled {
        color: orange;
      }
    }
  }

  .profile-title {
    text-align: left;
    color: #2e478d;
    font-size: 22px;
    padding-left: 10px;
  }

  .no-publications {
    color: #5f5f5f;
    font-size: 15px;
    text-align: center;
  }
  .bullet {
    color: #31649e;
    font-size: 24px;
    line-height: 27px;
    float: left;
  }

  .publication-name {
    font-weight: bold;
    padding-left: 2px;
  }
  .publication-info {
    color: #5d5d5d;
    font-size: 15px;
    padding-left: 10px;
  }

  .publication-item-container {
    display: flex;
    padding: 5px;
    border: 1px solid #a3b4c7;
    border-radius: 5px;
    margin-top: 5px;
    background: #eff7ff;

    .publication-item {
      width: 100%;
    }
  }
}
</style>
